import React, { useEffect, useState, useMemo } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useApp } from '../../hooks/useApp';
import useMqttTopics from '../../hooks/useMqttTopics';
import MegaFactoryContent from '../../components/MegaFactoryContent';
import Videoplayer from '../../components/Videoplayer';

const Proveit = () => {
  const { vtecAssetsUnsorted: vtecAssets, activeVideo } = useApp();
  const [activeMegaFactory, setActiveMegaFactory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!vtecAssets) return;

    const megafactoryId = 'zvvIoi5j80myiEVJLVj8';
    const foundMegaFactory = vtecAssets.find(
      (asset) => asset.id === megafactoryId && asset.Type === 'megafactory'
    );

    if (foundMegaFactory) {
      setActiveMegaFactory(foundMegaFactory);
    }
    setLoading(false);
  }, [vtecAssets]);

  // Pre-connect MQTT for all factories with data tags in the ProveIt megafactory
  // This ensures data is ready immediately when user switches to any factory tab
  const mediaFactoryConfig = useMemo(() => {
    if (!activeMegaFactory || !vtecAssets) return { mqttConfig: null, topicPaths: [] };
    
    // Get all factory IDs from the megafactory's suites
    const suites = activeMegaFactory.suites || [];
    const factoryIds = suites.map(s => s.factoryId).filter(Boolean);
    
    // Find all factories with realtime topics (data tags and/or video control topic)
    const allFactories = factoryIds
      .map(id => vtecAssets.find(asset => asset.id === id))
      .filter(Boolean);
    
    const factoriesWithRealtimeTopics = allFactories.filter(
      factory => (factory?.dataTags && factory.dataTags.length > 0) || Boolean(factory?.videoControlTopic)
    );
    
    if (factoriesWithRealtimeTopics.length === 0) return { mqttConfig: null, topicPaths: [] };
    
    // Use megafactory MQTT config first, then fallback to the first factory's own config
    const mqttConfig = (activeMegaFactory?.mqttConfig?.broker ? activeMegaFactory.mqttConfig : null) 
      || factoriesWithRealtimeTopics[0]?.mqttConfig;
    
    // Collect all topic paths from all factories (data tags + video control topics)
    const allTopicPaths = Array.from(new Set(
      factoriesWithRealtimeTopics.flatMap((factory) => ([
        ...(factory.dataTags || []).map(tag => tag.topicPath).filter(Boolean),
        ...(factory.videoControlTopic ? [factory.videoControlTopic] : []),
      ]))
    ));
    
    return { mqttConfig, topicPaths: allTopicPaths };
  }, [activeMegaFactory, vtecAssets]);

  // Pre-connect to MQTT when ProveIt page loads
  const { values: mqttValues, recentlyChanged: mqttRecentlyChanged } = useMqttTopics(
    mediaFactoryConfig.mqttConfig,
    mediaFactoryConfig.topicPaths
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          flexDirection: 'column',
          background: `linear-gradient(to right, #00263e, #003b71)`,
        }}
      >
        <CircularProgress size={100} thickness={5} sx={{ color: '#F2EEA1' }} />
        <Typography fontSize={'large'} color={'#fff'} marginTop={4}>
          Loading megafactory...
        </Typography>
      </Box>
    );
  }

  if (!activeMegaFactory) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          flexDirection: 'column',
          background: `linear-gradient(to right, #00263e, #003b71)`,
        }}
      >
        <Typography fontSize={'large'} color={'#fff'}>
          Megafactory not found
        </Typography>
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        background: `linear-gradient(to right, #00263e, #003b71)`, 
        height: '100vh', 
        width: '100%',
        overflow: 'hidden',
      }}
    >
      <MegaFactoryContent
        activeMegaFactory={activeMegaFactory}
        isModal={false}
        isFactoryDirect={true}
        preconnectedMqttValues={mqttValues}
        preconnectedMqttRecentlyChanged={mqttRecentlyChanged}
        hideDiscover={true}
      />
      {activeVideo && <Videoplayer />}
    </Box>
  );
};

export default Proveit;